/**
 * Scroll math for revealing a tamper highlight inside the PDF's scroll container
 * (SA-1812). Kept free of DOM so the arithmetic is testable — jsdom has no layout,
 * so a component test could never assert this.
 */

/**
 * The scroll offset that brings a box into view, centred, or null when no scroll
 * is warranted.
 *
 * Returns null when the box is already fully visible: scrolling then would yank
 * the viewport away from wherever the user had it for no gain.
 *
 * @param {{ boxTop: number, boxHeight: number, viewTop: number,
 *           viewHeight: number, scrollTop: number }} metrics
 *   `boxTop` / `viewTop` are viewport-relative (`getBoundingClientRect().top`);
 *   `viewHeight` is the container's visible height (`clientHeight`).
 * @returns {number|null}
 */
export const resolveRevealScrollTop = ({
  boxTop,
  boxHeight,
  viewTop,
  viewHeight,
  scrollTop,
}) => {
  const metrics = [boxTop, boxHeight, viewTop, viewHeight, scrollTop];
  if (!metrics.every((value) => Number.isFinite(value))) return null;
  // A container with no height has nothing to reveal into, and a zero-height box
  // has no position worth centring.
  if (viewHeight <= 0 || boxHeight <= 0) return null;

  const offsetWithinView = boxTop - viewTop;
  const isFullyVisible =
    offsetWithinView >= 0 && offsetWithinView + boxHeight <= viewHeight;
  if (isFullyVisible) return null;

  // A box taller than the viewport cannot be centred meaningfully — centring
  // would push its top edge off-screen and hide where the marking starts. Align
  // its top edge instead.
  const isOversized = boxHeight >= viewHeight;
  const target = isOversized
    ? scrollTop + offsetWithinView
    : scrollTop + offsetWithinView - (viewHeight - boxHeight) / 2;

  // Clamped so a box near the top of the document cannot ask for a negative
  // scroll offset.
  return Math.max(0, target);
};

/**
 * Nearest ancestor that actually scrolls vertically.
 *
 * Walked manually instead of using `scrollIntoView`, which scrolls EVERY
 * scrollable ancestor — that would also shift the claim form in the left pane
 * and the page behind it.
 */
export const findScrollableAncestor = (node) => {
  let current = node?.parentElement ?? null;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const canScroll = overflowY === 'auto' || overflowY === 'scroll';
    if (canScroll && current.scrollHeight > current.clientHeight)
      return current;
    current = current.parentElement;
  }

  return null;
};
